import React, { forwardRef, InputHTMLAttributes } from 'react';

import clsx from 'clsx';

import {
  radioWrapper,
  hiddenInput,
  radioButtonWrapper,
  radioButton,
  radioInnerCircle,
} from './RadioButton.css';
import { useRadioGroup } from './RadioGroup';
import { Flex } from '../Flex';
import { Text } from '../Text';

export interface RadioButtonProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  label?: React.ReactNode;
  value?: string;
}

export const RadioButton = forwardRef<HTMLInputElement, RadioButtonProps>(
  ({ label, checked, disabled, className, onChange, value, name, ...props }, ref) => {
    const group = useRadioGroup();

    // Group context가 있으면 group의 값을 사용
    const isChecked = group ? group.value === value : checked;
    const isDisabled = group?.disabled || disabled;
    const radioName = group?.name || name;

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      if (group?.onChange && value !== undefined) {
        group.onChange(value);
      }
      onChange?.(e);
    };

    return (
      <label className={clsx(radioWrapper, className)}>
        <input
          ref={ref}
          type="radio"
          className={hiddenInput}
          checked={isChecked}
          disabled={isDisabled}
          onChange={handleChange}
          name={radioName}
          value={value}
          {...props}
        />
        <Flex
          align="center"
          justify="center"
          className={radioButtonWrapper({ disabled: isDisabled || false })}
        >
          <div
            className={radioButton({
              checked: isChecked || false,
              disabled: isDisabled || false,
            })}
          >
            {isChecked && <div className={radioInnerCircle({ disabled: isDisabled || false })} />}
          </div>
        </Flex>
        {label &&
          (typeof label === 'string' ? (
            <Text textStyle="14_400" color="semanticPrimary">
              {label}
            </Text>
          ) : (
            label
          ))}
      </label>
    );
  },
);

RadioButton.displayName = 'RadioButton';
